import React, { useCallback, useEffect, useState } from 'react';
import Select, { PropsValue } from 'react-select';
import { AsyncPaginate } from 'react-select-async-paginate';

import styled from 'styled-components';

import { Color } from '@assets/constants';
import { RequiredAsterisk } from '@containers/Checkout/CheckoutContainer';
import { SpeedyCity, SpeedyOffice } from '@models/speedy';
import { useSpeedyCitiesByName, useSpeedyOfficesByCity } from '@mutations';
import { ActivityIndicator } from '@ui/ActivityIndicator';
import { Input } from '@ui/Input';
import { translateTextForSpeedyQuery } from '@utils/translateText';

import { getSpeedyOfficeName } from './utils';

export enum DeliveryOption {
  PERSONAL_ADDRESS = 'personalAddress',
  SPEEDY_OFFICE = 'speedyOffice'
}

interface Props {
  selectedDeliveryOption: string;
  customerCity: string;
  setCustomerCity: (city: string) => void;
  customerStreet: string;
  setCustomerStreet: (address: string) => void;
  customerAdditionalNotes: string;
  setCustomerAdditionalNotes: (notes: string) => void;
  setSelectedSpeedyOffice: (office: SpeedyOffice | null) => void;
  selectedCity: SpeedyCity | null;
  setSelectedCity: (city: SpeedyCity | null) => void;
}

export const DeliveryInfoSelector = ({
  selectedDeliveryOption,
  customerCity,
  setCustomerCity,
  customerStreet,
  setCustomerStreet,
  customerAdditionalNotes,
  setCustomerAdditionalNotes,
  setSelectedSpeedyOffice,
  selectedCity,
  setSelectedCity
}: Props) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [speedyOffices, setSpeedyOffices] = useState<SpeedyOffice[]>([]);

  const { mutateAsync: findCitiesByName, isPending: isFetchingCities } =
    useSpeedyCitiesByName();
  const { mutateAsync: findOfficesByCity, isPending: isFetchingOffices } =
    useSpeedyOfficesByCity();

  const loadCities = useCallback(async () => {
    const cities = await findCitiesByName(
      translateTextForSpeedyQuery(searchTerm)
    );

    return {
      options: cities ?? []
    };
  }, [searchTerm]);

  const loadOffices = useCallback(async (city: SpeedyCity) => {
    const offices = await findOfficesByCity(city);
    setSpeedyOffices(offices ?? []);
  }, []);

  const value = customerCity
    ? ({ name: customerCity } as PropsValue<SpeedyCity>)
    : undefined;

  useEffect(() => {
    if (customerCity) {
      setSelectedCity({
        name: customerCity,
        nameEn: translateTextForSpeedyQuery(customerCity.toLowerCase())
      });
    }
  }, []);

  useEffect(() => {
    if (selectedCity) {
      loadOffices(selectedCity);
    } else {
      setSpeedyOffices([]);
    }
  }, [selectedCity]);

  const inputStyle = `
    background: white;
    border: 1px solid rgb(209, 209, 209);
    border-radius: 2px;
    padding: 10px 0;
  `;

  const isLoading = isFetchingCities || isFetchingOffices;

  return (
    <>
      {selectedDeliveryOption === DeliveryOption.PERSONAL_ADDRESS && (
        <InputWrapper>
          <Text>
            Населено място <RequiredAsterisk />
          </Text>
          <AsyncPaginate
            value={value}
            loadOptions={loadCities}
            getOptionLabel={(option: SpeedyCity) => option.name}
            getOptionValue={(option: SpeedyCity) => option.name}
            placeholder={'Населено място'}
            loadingMessage={() => 'Търсим населено място...'}
            noOptionsMessage={() => 'Няма намерени резултати'}
            onInputChange={(value) => setSearchTerm(value.toLowerCase())}
            onChange={(value) => setCustomerCity(value?.name ?? '')}
            debounceTimeout={500}
          />
          {customerCity && (
            <>
              <Text>
                Улица <RequiredAsterisk />
              </Text>
              <Input
                value={customerStreet}
                placeholder={'улица, номер'}
                onChange={(e) => setCustomerStreet(e.target.value)}
                additionalStyles={inputStyle}
              />
              <Text>Допълнителни пояснения</Text>
              <Input
                value={customerAdditionalNotes}
                placeholder={'етаж, вход, апартамент, град, пощенски код'}
                onChange={(e) => setCustomerAdditionalNotes(e.target.value)}
                additionalStyles={inputStyle}
              />
            </>
          )}
        </InputWrapper>
      )}
      {selectedDeliveryOption === DeliveryOption.SPEEDY_OFFICE && (
        <InputWrapper>
          <Text>
            Населено място <RequiredAsterisk />
          </Text>
          <AsyncPaginate
            value={value}
            loadOptions={loadCities}
            getOptionLabel={(option: SpeedyCity) => option.name}
            getOptionValue={(option: SpeedyCity) => option.name}
            placeholder={'Населено място'}
            loadingMessage={() => 'Търсим населено място...'}
            noOptionsMessage={() => 'Няма намерени резултати'}
            onInputChange={(value) => setSearchTerm(value.toLowerCase())}
            onChange={(value) => {
              setSelectedCity(value);
              setCustomerCity(value?.name ?? '');
            }}
            debounceTimeout={500}
          />
          {isLoading && selectedCity && (
            <ActivityIndicatorWrapper>
              <ActivityIndicator size={45} color={Color.ACCENT} />
              <p>Търсим офиси около вас...</p>
            </ActivityIndicatorWrapper>
          )}
          {!isLoading && selectedCity && (
            <>
              <Text>
                Офис <RequiredAsterisk />
              </Text>
              <Select
                options={speedyOffices}
                getOptionLabel={(option: SpeedyOffice) =>
                  getSpeedyOfficeName(option)
                }
                getOptionValue={(option: SpeedyOffice) =>
                  getSpeedyOfficeName(option)
                }
                placeholder={'Офис'}
                loadingMessage={() => 'Търсим офис...'}
                noOptionsMessage={() => 'Няма намерени резултати'}
                onChange={(value) => setSelectedSpeedyOffice(value)}
              />
            </>
          )}
        </InputWrapper>
      )}
    </>
  );
};

const Text = styled.p`
  font-size: 16px;
  font-weight: 500;
  margin-left: 5px;
  color: ${Color.DARK_GRAY};
`;

const ActivityIndicatorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 38px;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
